import {
  ButtonWithText,
  TextInput,
  FlexBox,
  ButtonTheme,
  FontClass,
  TextButton,
  ColorClass,
  notifyError,
  MailIcon,
  ColorValue,
  Body3Medium,
  IconAndLabel,
} from "ui";
import { useState } from "react";
import { LoginOrRegister } from "@web/types/LoginOrRegister";
import { isValidEmail } from "shared";
import { postLoginEmail } from "@web/utils/rest/login/postLoginEmail";
import { isFromNative } from "@web/utils/env/isFromNative";
import { padUrlParamsWithToken } from "@web/utils/auth/PadUrlParamsWithToken";

type Props = {
  loginOrRegister: LoginOrRegister;
  onSuccess: () => void;
};

/**
 * A form that allows users to input their email.
 *
 * For passwordless login, i.e. email nonce login.
 *
 * Used for both login and registration.
 */
export function LoginOrRegisterEmailForm({
  loginOrRegister,
  onSuccess,
}: Props) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fromNative = isFromNative();

  return (
    <FlexBox alignItems="center" flexDirection="column" gap={24}>
      <TextInput
        label={
          <IconAndLabel
            icon={<MailIcon colorValue={ColorValue.Secondary} />}
            label={
              <Body3Medium
                colorClass={ColorClass.Secondary}
                textTransform="uppercase"
              >
                Email address
              </Body3Medium>
            }
          />
        }
        onChange={setEmail}
        placeholder="Enter your email"
        value={email}
      />
      <ButtonWithText
        buttonTheme={ButtonTheme.Highlight}
        disabled={!isValidEmail(email)}
        fontClass={FontClass.Body1}
        isLoading={isLoading}
        onClick={async () => {
          setIsLoading(true);
          const data = await postLoginEmail({
            email,
          });

          if (data == null) {
            setIsLoading(false);
            notifyError();
            return;
          }

          if (data.redirect != null) {
            window.location.href = padUrlParamsWithToken(data);
            return;
          }

          setIsLoading(false);
          if (data.errorDescription != null) {
            notifyError(data.errorDescription, data.errorMessage);
            return;
          }

          onSuccess();
        }}
        style={{ width: "100%" }}
      >
        {loginOrRegister === "login" ? "Sign In" : "Create Account"}
      </ButtonWithText>
      <TextButton
        buttonThemeOrColorClass={ColorClass.Secondary}
        fontClass={FontClass.Body2}
        href={
          fromNative
            ? loginOrRegister === "login"
              ? "/native/register"
              : "/native/login"
            : loginOrRegister === "login"
            ? "/register"
            : "/login"
        }
        type="link_internal"
      >
        {loginOrRegister === "login"
          ? "Don't have an account? Register here"
          : "Already have an account? Sign in here"}
      </TextButton>
    </FlexBox>
  );
}
